import { type CSSProperties } from "react";

/** Minimum line-height for headings and uppercase labels that may contain Swedish Å/Ä/Ö. */
export const DIACRITIC_SAFE_LINE_HEIGHT = 1.35;

/** Top padding so diacritic rings stay inside the line box when parents use overflow clipping. */
export const DIACRITIC_SAFE_PADDING_TOP = "0.15em";

function formatCssLineHeight(lineHeight: string | number): string {
  return typeof lineHeight === "number" ? String(lineHeight) : lineHeight;
}

/**
 * Mantine Title reads font-size and line-height from `--title-fz` / `--title-lh`.
 * Override those whenever custom sizing, uppercase, or lineClamp could clip diacritics.
 */
export function toMantineTitleStyle(style: CSSProperties = {}): CSSProperties {
  const lineHeight = style.lineHeight ?? DIACRITIC_SAFE_LINE_HEIGHT;
  const paddingTop = style.paddingTop ?? DIACRITIC_SAFE_PADDING_TOP;
  const fontSize = style.fontSize;

  return {
    ...style,
    lineHeight,
    paddingTop,
    ["--title-lh" as string]: formatCssLineHeight(lineHeight),
    ...(fontSize ? { fontSize, ["--title-fz" as string]: fontSize } : {}),
  };
}

export function diacriticSafeTitleStyle(overrides: CSSProperties = {}): CSSProperties {
  return toMantineTitleStyle({
    lineHeight: DIACRITIC_SAFE_LINE_HEIGHT,
    paddingTop: DIACRITIC_SAFE_PADDING_TOP,
    ...overrides,
  });
}

/** Mantine Text props for localized uppercase labels. */
export const diacriticSafeUppercaseTextProps = {
  lh: DIACRITIC_SAFE_LINE_HEIGHT,
  style: { paddingTop: DIACRITIC_SAFE_PADDING_TOP } satisfies CSSProperties,
} as const;

/** Mantine Badge label styles for uppercase status strings. */
export const diacriticSafeBadgeLabelStyles = {
  label: {
    lineHeight: String(DIACRITIC_SAFE_LINE_HEIGHT),
    paddingTop: "0.05em",
  },
} as const;

/** Home greeting — scaled from Figma 32/36 for wider layout. */
export const facilitatorHomeGreetingTypography = diacriticSafeTitleStyle({
  fontSize: 40,
  lineHeight: "44px",
  fontWeight: 700,
  letterSpacing: "-0.06em",
});

/** Promo hero on home templates card — same weight as greeting, one step down in size. */
export const facilitatorHomePromoTitleTypography = diacriticSafeTitleStyle({
  fontSize: 32,
  lineHeight: "36px",
  fontWeight: 700,
  letterSpacing: "-0.06em",
});
